import axios from 'axios'
import React, { useContext, useState } from 'react'
import { AppContext } from '../App'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import DataTable from 'react-data-table-component';
import moment from "moment"
import * as XLSX from 'xlsx';
import { CircularProgress } from '@mui/material';
import { url as urlPayload, urlDashboard, urlDashboardSanta } from '../BaseUrl';

const AUTH = 'Basic ZGFzaDokJSREJEFTSA==';
const LIMIT = 1000;

function formatDate(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function generateDates(start, end) {
    const dates = [];
    let current = new Date(start);
    const endDate = new Date(end);
    current.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    while (current <= endDate) {
        dates.push(formatDate(current));
        current.setDate(current.getDate() + 1);
    }
    return dates;
}

async function fetchAllPagesForDate(clientId, date, baseUrl, isSantaClause, onProgress) {
    if (isSantaClause) {
        const results = [];
        const seenTokens = new Set();
        let nextToken = "";
        let page = 1;
        while (true) {
            const fetchUrl = `${baseUrl}/current-accounts-settled-page?uid=${encodeURIComponent(clientId)}&specific_day=${date}&limit=${LIMIT}${nextToken ? `&nextPageToken=${encodeURIComponent(nextToken)}` : ""}`;
            const res = await axios.get(fetchUrl, { headers: { Authorization: AUTH } });
            const list = res.data?.list || [];
            results.push(...list);
            if (onProgress) {
                onProgress(`Memuat ${date}: ${results.length.toLocaleString("id-ID")} data (halaman ${page})...`);
            }
            nextToken = res.data?.nextPageToken;
            if (!nextToken || list.length === 0 || seenTokens.has(nextToken)) {
                break;
            }
            seenTokens.add(nextToken);
            page++;
        }
        return results;
    }

    const results = [];
    const seenIds = new Set();
    let page = 1;
    while (true) {
        const fetchUrl = `${baseUrl}/current-accounts-settled-limit-pages?client_id=${encodeURIComponent(clientId)}&specific_day=${date}&page=${page}&limit=${LIMIT}`;
        const res = await axios.get(fetchUrl, { headers: { Authorization: AUTH } });
        const list = res.data?.data || [];
        if (list.length === 0) break;
        let newItems = 0;
        for (const item of list) {
            const id = item.id || item.data?.ID;
            if (id && !seenIds.has(id)) {
                seenIds.add(id);
                results.push(item);
                newItems++;
            }
        }
        if (onProgress) {
            onProgress(`Memuat ${date}: ${results.length.toLocaleString("id-ID")} data (halaman ${page})...`);
        }
        if (newItems === 0) break;
        page++;
    }
    return results;
}

function mapToRow(item) {
    return {
        piroBase: {
            transactionDate: item.metadata?.createdDate,
            transactionId: item.data?.TX || '',
            referenceId: item.data?.referenceId || '',
            debit: item.data?.debit || 0,
            credit: item.data?.credit || 0,
        },
        type: item.data?.transactionType || ''
    };
}

export default function Mutation() {
    const [transaction, setTransaction] = useState([])
    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)
    const [error, setError] = useState("")
    const [loading, setLoading] = useState(false)
    const [progressText, setProgressText] = useState("")
    const context = useContext(AppContext);
    const urlParams = new URLSearchParams(window.location.search)
    const clientId = urlParams.get('clientId') || (context.nmid || "").split(":")[1]

    function exportExcel() {
        if (transaction.length === 0) return
        const rows = transaction.map(row => {
            let orderId = row.piroBase.referenceId || row.piroBase.transactionId || '';
            if (typeof orderId === 'string' && (orderId.toUpperCase().includes('REFUND') || row.type?.toUpperCase().includes('REFUND'))) {
                orderId = orderId.replace(/REFUND[-_]?/gi, '');
            }
            return {
                'Transaction Date': moment(row.piroBase.transactionDate).format("DD-MM-YYYY HH:mm:ss"),
                'TX': row.piroBase.transactionId,
                'Type': row.type,
                'Order ID': orderId,
                'Debit': row.piroBase.debit,
                'Credit': row.piroBase.credit,
            };
        })
        const ws = XLSX.utils.json_to_sheet(rows)
        ws['!cols'] = [{ wch: 20 }, { wch: 50 }, { wch: 25 }, { wch: 35 }, { wch: 15 }, { wch: 15 }]
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Mutation')
        XLSX.writeFile(wb, `mutation_${clientId}_${formatDate(startDate)}_${formatDate(endDate)}.xlsx`)
    }

    async function fetchMutation(event) {
        event.preventDefault()
        if (!startDate || !endDate) {
            setError("Mohon isi tanggal terlebih dahulu")
            return
        }
        setError("")
        setTransaction([])
        setLoading(true)
        try {
            const paramNmid = urlParams.get('nmid')
            const paramClientId = urlParams.get('clientId')
            let isSantaClause = false
            if (paramNmid && paramClientId) {
                try {
                    const nmidInfoRes = await axios.get(`${urlPayload}/get-nmid-info?nmid=${paramNmid}&clientId=${paramClientId}`)
                    isSantaClause = nmidInfoRes.data?.isSantaClause ?? false
                } catch (e) {
                    isSantaClause = false
                }
            }
            const baseUrl = isSantaClause ? urlDashboardSanta : urlDashboard
            const dates = generateDates(startDate, endDate)
            setProgressText("Memulai pengambilan data...")
            const dateResults = await Promise.all(
                dates.map(date => fetchAllPagesForDate(clientId, date, baseUrl, isSantaClause, msg => setProgressText(msg)))
            )
            const allItems = dateResults.flat()

            const refundItems = allItems.filter(item => {
                const type = item.data?.transactionType || ''
                const tx = item.data?.TX || ''
                return type.toUpperCase().includes('REFUND') || tx.toUpperCase().includes('REFUND')
            })
            const refundTrackMap = {}
            if (refundItems.length > 0) {
                setProgressText(`Melacak ${refundItems.length} transaksi refund...`)
                const uniqueRefIds = [...new Set(refundItems.map(item => item.data?.referenceId).filter(Boolean))]
                for (let i = 0; i < uniqueRefIds.length; i += 10) {
                    const batch = uniqueRefIds.slice(i, i + 10)
                    await Promise.all(batch.map(async refId => {
                        try {
                            const res = await axios.get(`${baseUrl}/track-current-account?tx=${refId}`, { headers: { Authorization: AUTH } })
                            const currentAccount = res.data?.data?.currentAccount || []
                            if (currentAccount.length > 0) {
                                const originalRefId = currentAccount[0].data?.referenceId
                                if (originalRefId) {
                                    refundTrackMap[refId] = originalRefId
                                }
                            }
                        } catch (err) {
                            console.error(`Failed to track refund for referenceId ${refId}:`, err)
                        }
                    }))
                }
            }

            const mappedItems = allItems.map(mapToRow)
            mappedItems.forEach(row => {
                if (row.type?.toUpperCase().includes('REFUND') || row.piroBase?.transactionId?.toUpperCase().includes('REFUND')) {
                    let originalTx = row.piroBase?.transactionId || '';
                    originalTx = originalTx.replace(/[-_]REFUND.*/gi, '');
                    if (refundTrackMap[originalTx]) {
                        row.piroBase.referenceId = refundTrackMap[originalTx];
                    } else {
                        const originalDoc = mappedItems.find(t => t.piroBase?.transactionId === originalTx);
                        if (originalDoc) {
                            row.piroBase.referenceId = originalDoc.piroBase?.referenceId || originalDoc.piroBase?.transactionId || '';
                        } else {
                            if (row.piroBase.referenceId) {
                                row.piroBase.referenceId = row.piroBase.referenceId.replace(/REFUND[-_]?/gi, '').replace(/[-_]?REFUND/gi, '');
                            }
                        }
                    }
                }
            })

            setTransaction(mappedItems)
        } catch (err) {
            setError("Gagal mengambil data: " + (err.message || ""))
        } finally {
            setLoading(false)
            setProgressText("")
        }
    }

    return (
        <div style={{ padding: "20px", width: "100%" }}>
            <h2>Mutation</h2>
            <i style={{ fontSize: "12px", color: "red"}}>Mutasi harian maksimum 31 hari yang lalu</i>
            <div style={{ marginBottom: "20px" }}>
                <form onSubmit={fetchMutation}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                    <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker label="Start Date" format="DD-MM-YYYY" onChange={e => setStartDate(moment(e).toDate())}/>
                        </LocalizationProvider>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker label="End Date" format="DD-MM-YYYY" onChange={e => setEndDate(moment(e).toDate())}/>
                        </LocalizationProvider>
                    </div>
                        <button style={{ padding: "0px 30px 0px 30px", marginTop: "20px", marginLeft: "10px", height: "53px" }} type='submit' disabled={loading}>
                            {loading ? 'Loading...' : 'Submit'}
                        </button>
                        <button
                            type="button"
                            onClick={exportExcel}
                            disabled={transaction.length === 0}
                            style={{ padding: "0px 20px", marginTop: "20px", marginLeft: "10px", height: "53px", backgroundColor: "#97da76", border: "1px solid #7bc45e", cursor: transaction.length === 0 ? "not-allowed" : "pointer" }}
                        >
                            Export Excel
                        </button>
                    </div>
                </form>
            </div>
            {progressText && <p style={{ color: "#1976d2", margin: "10px 0", fontWeight: "500" }}>{progressText}</p>}
            {error && <p style={{ color: 'red' }}>{error}</p>}
            <DataTable
                columns={[
                    {
                        name: 'Transaction Date',
                        selector: row => moment(row.piroBase.transactionDate).format("DD-MM-YYYY HH:mm:ss"),
                        width: "175px",
                        style: {
                            color: 'rgba(0,0,0,.54)',
                        },
                    },
                    {
                        name: 'TX',
                        selector: row => row.piroBase.transactionId,
                        width: "490px",
                        style: {
                            color: 'rgba(0,0,0,.54)',
                        },
                    },
                    {
                        name: 'Type',
                        selector: row => row.type,
                        style: {
                            color: 'rgba(0,0,0,.54)',
                        },
                    },
                    {
                        name: 'Order ID',
                        selector: row => {
                            let orderId = row?.piroBase?.referenceId || row?.piroBase?.transactionId || '';
                            if (typeof orderId === 'string' && (orderId.toUpperCase().includes('REFUND') || row.type?.toUpperCase().includes('REFUND'))) {
                                return orderId.replace(/REFUND[-_]?/gi, '');
                            }
                            return orderId;
                        },
                        width: "335px",
                        style: {
                            color: 'rgba(0,0,0,.54)',
                        },
                    },
                    {
                        name: 'Debit',
                        selector: row => !row.piroBase.debit ? <span>Rp 0</span> : <span style={{ color: "red" }}>{row.piroBase.debit.toLocaleString(
                            "id-ID",
                            { style: "currency", currency: "IDR" }
                        )}</span>,
                        style: {
                            color: 'rgba(0,0,0,.54)',
                        },
                    },
                    {
                        name: 'Credit',
                        selector: row => !row.piroBase.credit ? <span>Rp 0</span> : <span style={{ color: "green" }}>{row.piroBase.credit.toLocaleString(
                            "id-ID",
                            { style: "currency", currency: "IDR" }
                        )}</span>,
                        style: {
                            color: 'rgba(0,0,0,.54)',
                        },
                    },
                ]}
                data={transaction}
                progressPending={loading}
                progressComponent={
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: '16px' }}>
                        <CircularProgress size={40} />
                        <span style={{ fontSize: '15px', color: '#1976d2', fontWeight: 500 }}>
                            {progressText || 'Memuat data mutasi...'}
                        </span>
                    </div>
                }
                noDataComponent={
                    <div style={{ padding: '40px', color: '#666', fontSize: '14px', textAlign: 'center' }}>
                        Tidak ada data untuk ditampilkan
                    </div>
                }
                pagination
                paginationPerPage={25}
                paginationRowsPerPageOptions={[10, 25, 50, 100, 500]}
                highlightOnHover
                pointerOnHover
            />
        </div>
    )
}
